// Flowbite-based App component - Modern Tailwind dashboard

import React, { Suspense, Component, ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BarChart3, TrendingUp, Clock, Search, Filter, Download, Plus, Settings, Users, Target, DollarSign, Calendar, Bell, Menu, X, Zap, MessageCircle, Brain } from 'lucide-react';
import { PipelineOverview } from './components/flowbite/PipelineOverview';
import { LeadsCRUDInterface } from './components/crud/LeadsCRUD';
import { OpportunitiesCRUDInterface } from './components/crud/OpportunitiesCRUD';
import { GTMSystemsOverview } from './components/gtm/GTMSystemsOverview';
import { SlackIntegration } from './components/integrations/SlackIntegration';
import { SixSenseIntegration } from './components/integrations/SixSenseIntegration';
import { AgentDashboard } from './components/ai/AgentDashboard';
import { AgentInteraction } from './components/ai/AgentInteraction';
import { gtmAgentService } from './services/ai/agentService';
import unifiedDataService from './services/unifiedDataService';


// Simple ErrorBoundary implementation
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  FallbackComponent?: React.ComponentType<{ error: Error; resetError: () => void }>;
  onReset?: () => void;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md">
            <div>
              <span className="font-medium text-red-800">Application Error</span>
              <div className="mt-2 text-sm text-red-700">
                The application encountered an unexpected error. Please refresh the page or try again later.
              </div>
              <button
                onClick={this.resetError}
                className="mt-3 text-sm bg-red-100 hover:bg-red-200 px-3 py-1 rounded text-red-800"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Create QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message.includes('4')) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// App Error Fallback
const AppErrorFallback: React.FC<{ error: Error; resetError: () => void }> = ({
  error,
  resetError,
}) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-lg">
      <div>
        <span className="font-medium text-red-800">Application Error</span>
        <div className="mt-2 text-sm text-red-700">
          The application encountered an unexpected error. Please refresh the page or try again later.
        </div>
        <button
          onClick={resetError}
          className="mt-3 text-sm bg-red-100 hover:bg-red-200 px-3 py-1 rounded text-red-800"
        >
          Reload App
        </button>
      </div>
    </div>
  </div>
);

// App Loading
const AppLoading: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
    <h2 className="text-xl font-semibold text-gray-700">Loading Sales Dashboard...</h2>
  </div>
);

// Main Dashboard
const DashboardApp: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [activeView, setActiveView] = React.useState('ai-agents');
  const [aiAnalysisMessage, setAiAnalysisMessage] = React.useState<string>('');
  const [slackMessage, setSlackMessage] = React.useState<string>('');

  return (
    <div className="dark min-h-screen bg-gray-900 dark:bg-gray-900">
      {/* Top Navigation Bar */}
      <nav className="bg-gray-800/95 dark:bg-gray-800/95 backdrop-blur-sm border-b border-gray-700 dark:border-gray-700 fixed w-full z-30 top-0 shadow-lg">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-start">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="inline-flex items-center p-2 text-sm text-gray-400 dark:text-gray-400 rounded-lg md:hidden hover:bg-gray-700 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-600"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-netlify-gradient rounded-lg flex items-center justify-center mr-3 ml-2 md:mr-3">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <span className="text-xl md:text-2xl font-bold text-white dark:text-white bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                    Netlify GTM
                  </span>
                  <div className="text-xs text-gray-400 dark:text-gray-400 font-medium tracking-wide">Revenue Operations</div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Search */}
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Search className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <input
                    type="search"
                    className="bg-gray-700 dark:bg-gray-700 border border-gray-600 dark:border-gray-600 text-white dark:text-white text-sm rounded-full focus:ring-blue-500 focus:border-blue-500 block w-80 pl-10 p-2.5 placeholder-gray-400 dark:placeholder-gray-400 transition-all duration-300 focus:shadow-md"
                    placeholder="Search leads, deals, contacts..."
                  />
                </div>
              </div>

              {/* Notifications */}
              <button className="p-2 text-gray-400 dark:text-gray-400 rounded-full hover:text-blue-400 hover:bg-gray-700 dark:hover:bg-gray-700 relative transition-all duration-300 hover:shadow-sm">
                <Bell className="w-5 h-5" />
                <div className="absolute top-1 right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse-slow"></div>
              </button>

              {/* Profile */}
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-netlify-gradient rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white text-sm font-bold">JD</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 w-64 h-screen pt-20 transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} bg-gray-50 dark:bg-gray-800 backdrop-blur-sm border-r border-gray-200 dark:border-gray-700 md:translate-x-0 shadow-xl`}>
        <div className="h-full px-3 pb-4 overflow-y-auto">
          <SidebarNavigation activeView={activeView} setActiveView={setActiveView} />
        </div>
      </aside>

      {/* Main Content */}
      <div className="p-6 md:p-8 md:ml-64 h-screen pt-20 lg:pt-24 flex flex-col">
        <div className="flex-1 min-h-0">
          <MainContent activeView={activeView} setActiveView={setActiveView} aiAnalysisMessage={aiAnalysisMessage} setAiAnalysisMessage={setAiAnalysisMessage} />
        </div>
      </div>
    </div>
  );
};

// Sidebar Navigation
const SidebarNavigation: React.FC<{ activeView: string; setActiveView: (view: string) => void }> = ({ activeView, setActiveView }) => {
  const menuItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'opportunities', label: 'Opportunities', icon: Target },
    { id: 'ai-agents', label: 'AI Agents', icon: Brain },
    { id: 'gtm-systems', label: 'GTM Systems', icon: Zap },
    { id: 'integrations', label: 'Integrations', icon: MessageCircle },
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <ul className="space-y-2 font-medium">
      {menuItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeView === item.id;

        return (
          <li key={item.id}>
            <button
              onClick={() => setActiveView(item.id)}
              className={`flex items-center w-full p-3 rounded-lg transition-all duration-300 group transform hover:-translate-y-0.5 ${
                isActive
                  ? 'bg-blue-700 dark:bg-blue-600 text-white shadow-lg'
                  : 'text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 hover:shadow-sm'
              }`}
            >
              <Icon className={`w-5 h-5 transition duration-200 ${
                isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-blue-700 dark:group-hover:text-blue-400'
              }`} />
              <span className="ml-3 font-medium">{item.label}</span>
              {isActive && (
                <div className="ml-auto w-2 h-2 bg-white rounded-full animate-bounce-gentle"></div>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

// Main Content Router
const MainContent: React.FC<{ activeView: string; setActiveView: (view: string) => void; aiAnalysisMessage: string; setAiAnalysisMessage: (message: string) => void; slackMessage: string; setSlackMessage: (message: string) => void }> = ({ activeView, setActiveView, aiAnalysisMessage, setAiAnalysisMessage, slackMessage, setSlackMessage }) => {
  switch (activeView) {
    case 'overview':
      return <OverviewContent />;
    case 'leads':
      return <LeadsContent setActiveView={setActiveView} setAiAnalysisMessage={setAiAnalysisMessage} setSlackMessage={setSlackMessage} />;
    case 'opportunities':
      return <OpportunitiesContent />;
    case 'ai-agents':
      return <AIAgentsContent aiAnalysisMessage={aiAnalysisMessage} setAiAnalysisMessage={setAiAnalysisMessage} />;
    case 'gtm-systems':
      return <GTMSystemsContent />;
    case 'integrations':
      return <IntegrationsContent slackMessage={slackMessage} setSlackMessage={setSlackMessage} />;
    case 'revenue':
      return <RevenueContent />;
    case 'calendar':
      return <CalendarContent />;
    case 'settings':
      return <SettingsContent />;
    default:
      return <OverviewContent />;
  }
};

// Overview Content
const OverviewContent: React.FC = () => (
  <>
    <ErrorBoundary>
      <Suspense fallback={<DashboardLoadingSkeleton />}>
        <PipelineOverview
          className="mb-6 lg:mb-8"
          onMetricClick={(metric) => {
            console.log('Metric clicked:', metric);
          }}
          refreshInterval={300000}
        />
      </Suspense>
    </ErrorBoundary>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mt-6 lg:mt-8">
      <ErrorBoundary>
        <Suspense fallback={<SectionLoadingSkeleton />}>
          <RecentActivity />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary>
        <Suspense fallback={<SectionLoadingSkeleton />}>
          <TopPerformers />
        </Suspense>
      </ErrorBoundary>
    </div>
  </>
);

// Leads Content
const LeadsContent: React.FC<{ setActiveView: (view: string) => void; setAiAnalysisMessage: (message: string) => void; setSlackMessage: (message: string) => void }> = ({ setActiveView, setAiAnalysisMessage, setSlackMessage }) => (
  <LeadsCRUDInterface
    onAnalyzeWithAI={(leadData) => {
      setAiAnalysisMessage(`Please analyze this lead: ${leadData.name} from ${leadData.company} (${leadData.email}). Status: ${leadData.status}, Value: $${leadData.value.toLocaleString()}, Source: ${leadData.source}. Provide qualification score and recommendations.`);
      setActiveView('ai-agents');
    }}
    onSendToSlack={(leadData) => {
      if (setSlackMessage) {
        setSlackMessage(`🎯 New Lead Alert!\n\n👤 **${leadData.name}** from **${leadData.company}**\n📧 ${leadData.email}\n📞 ${leadData.phone}\n💰 Value: $${leadData.value.toLocaleString()}\n📊 Status: ${leadData.status}\n📈 Source: ${leadData.source}\n\nReady for follow-up! 🚀`);
        setActiveView('integrations');
      }
    }}
  />
);

// Opportunities Content
const OpportunitiesContent: React.FC = () => (
  <OpportunitiesCRUDInterface />
);

// AI Agents Content
const AIAgentsContent: React.FC<{ aiAnalysisMessage: string; setAiAnalysisMessage: (message: string) => void }> = ({ aiAnalysisMessage, setAiAnalysisMessage }) => {
  const handleResult = (result: any) => {
    console.log('Agent execution result:', result);
    // Clear the analysis message after it's been used
    setAiAnalysisMessage('');
  };

  return (
    <div className="h-full">
      <AgentInteraction
        className="h-full"
        onResult={handleResult}
        preFilledMessage={aiAnalysisMessage}
      />
    </div>
  );
};

// GTM Systems Content
const GTMSystemsContent: React.FC = () => (
  <GTMSystemsOverview />
);

// Integrations Content
const IntegrationsContent: React.FC<{ slackMessage: string; setSlackMessage: (message: string) => void }> = ({ slackMessage, setSlackMessage }) => (
  <div className="space-y-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Integrations</h1>
      <p className="text-gray-600 dark:text-gray-400 mt-2">
        Connect your favorite tools and services to streamline your sales workflow.
      </p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <SlackIntegration
        preFilledMessage={slackMessage}
        onMessageSent={(channelId, message) => {
          console.log(`Message sent to ${channelId}:`, message);
          console.log('setSlackMessage function:', typeof setSlackMessage);
          if (typeof setSlackMessage === 'function') {
            setSlackMessage(''); // Clear the pre-filled message after sending
          } else {
            console.error('setSlackMessage is not a function:', setSlackMessage);
          }
        }}
        onError={(error) => {
          console.error('Slack integration error:', error);
        }}
      />
      <SixSenseIntegration
        onEnrichmentComplete={(data) => {
          console.log('Enrichment completed:', data);
        }}
        onError={(error) => {
          console.error('6sense integration error:', error);
        }}
      />
    </div>
  </div>
);

// Revenue Content
const RevenueContent: React.FC = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">Revenue Analytics</h2>
    <p className="text-gray-600">Revenue tracking and forecasting tools coming soon...</p>
  </div>
);

// Calendar Content
const CalendarContent: React.FC = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">Sales Calendar</h2>
    <p className="text-gray-600">Calendar integration for meetings and follow-ups coming soon...</p>
  </div>
);

// Settings Content
const SettingsContent: React.FC = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">Dashboard Settings</h2>
    <p className="text-gray-600">Configuration options and preferences coming soon...</p>
  </div>
);

// Recent Activity component
const RecentActivity: React.FC = () => {
  const [activities, setActivities] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const loadActivityData = async () => {
      try {
        const realActivities = await unifiedDataService.getActivityFeed(8);
        setActivities(realActivities);
      } catch (error) {
        console.error('Failed to load activity data:', error);
        // Fallback to mock data
        setActivities([
          { user: 'Alice Johnson', action: 'closed deal "Enterprise Software Deal - 15"', time: '2 hours ago', amount: '$45,000', avatar: 'AJ', color: 'bg-blue-500' },
          { user: 'Bob Smith', action: 'added new opportunity "Cloud Migration - 23"', time: '4 hours ago', amount: '$67,500', avatar: 'BS', color: 'bg-green-500' },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    loadActivityData();
  }, []);

  if (isLoading) {
    return (
      <div className="netlify-card">
        <div className="p-6">
          <h2 className="netlify-heading-3 mb-6">Recent Activity</h2>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse flex items-center space-x-4 p-4 bg-gray-100 rounded-xl">
                <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="netlify-card">
      <div className="p-6">
        <h2 className="netlify-heading-3 mb-6">Recent Activity • Real-time from API</h2>
        <div className="space-y-4">
          {activities.map((activity, index) => (
            <div key={index} className="flex items-center space-x-4 p-4 bg-gradient-to-r from-netlify-teal-50/50 to-white rounded-xl border border-netlify-teal-100/50 hover:shadow-sm transition-shadow duration-200">
              <div className={`w-10 h-10 ${activity.color} rounded-full flex items-center justify-center text-white font-semibold`}>
                {activity.avatar}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  <span className="font-semibold">{activity.user}</span> {activity.action}
                </p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">{activity.time}</span>
                  <span className="bg-netlify-teal-100 text-netlify-teal-800 text-xs font-medium px-3 py-1 rounded-full">{activity.amount}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Top Performers component
const TopPerformers: React.FC = () => {
  const performers = [
    { name: 'Alice Johnson', role: 'Sales Manager', amount: '$124,500', avatar: 'AJ', color: 'bg-green-500', rank: 1 },
    { name: 'Carol Williams', role: 'Sales Rep', amount: '$98,750', avatar: 'CW', color: 'bg-blue-500', rank: 2 },
    { name: 'Bob Smith', role: 'Sales Rep', amount: '$87,250', avatar: 'BS', color: 'bg-orange-500', rank: 3 },
  ];

  return (
    <div className="netlify-card">
      <div className="p-6">
        <h2 className="netlify-heading-3 mb-6">Top Performers</h2>
        <div className="space-y-4">
          {performers.map((performer, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-netlify-coral-50/30 to-white rounded-xl border border-netlify-coral-100/50 hover:shadow-sm transition-shadow duration-200">
              <div className="flex items-center space-x-4">
                <div className={`w-10 h-10 ${performer.color} rounded-full flex items-center justify-center text-white font-semibold`}>
                  {performer.avatar}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{performer.name}</p>
                  <p className="text-xs text-gray-500">{performer.role}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-netlify-primary">{performer.amount}</p>
                <p className="text-xs text-gray-500">This month</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Loading Skeletons
const DashboardLoadingSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow border">
          <div className="p-6 animate-pulse">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2">
        <div className="bg-white rounded-lg shadow border h-96">
          <div className="p-6 animate-pulse">
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
      <div className="xl:col-span-1">
        <div className="bg-white rounded-lg shadow border h-96">
          <div className="p-6 animate-pulse space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SectionLoadingSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow border">
    <div className="p-6 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-32 mb-6"></div>
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-100 rounded"></div>
        ))}
      </div>
    </div>
  </div>
);

// Main App
const App: React.FC = () => {
  return (
    <ErrorBoundary
      FallbackComponent={AppErrorFallback}
      onReset={() => {
        queryClient.clear();
        window.location.reload();
      }}
    >
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<AppLoading />}>
          <DashboardApp />
        </Suspense>

        {import.meta.env.DEV && (
          <ReactQueryDevtools
            initialIsOpen={false}
            position="bottom-right"
          />
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;